import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getUserName, getUserGroups, GROUP_LABELS } from "../shared/auth/msalConfig";
import { useApps } from "../shared/api/useApps";
import { T } from "../shared/styles/theme";
import NotificationBell from "./NotificationBell";

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return mobile;
}

export default function ShellLayout() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const name = getUserName(account);
  const groups = getUserGroups(account);
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const { data: apps } = useApps();
  const [openGroup, setOpenGroup] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);
  const profileRef = useRef(null);

  const dashboardApps = useMemo(
    () => (apps || []).filter((a) => a.category === "dashboard"),
    [apps]
  );

  // Group order — only show groups that have at least one visible app
  const MENU_GROUP_ORDER = ["Fund Management", "BD & Product", "Operations", "Finance", "Research", "Dealing", "Private Equity", "Admin", "IT", "Compliance"];
  const groupedApps = useMemo(() => {
    const map = {};
    dashboardApps.forEach((app) => {
      const g = app.menu_group || "Other";
      if (!map[g]) map[g] = [];
      map[g].push(app);
    });
    return map;
  }, [dashboardApps]);
  const visibleGroups = MENU_GROUP_ORDER.filter((g) => groupedApps[g]?.length > 0);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    if (openGroup || profileOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openGroup, profileOpen]);

  // Close mobile menu on navigate
  useEffect(() => {
    setMenuOpen(false);
    setOpenGroup(null);
    setProfileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    instance.logoutPopup().catch(() => instance.logoutRedirect());
  };

  const navTo = useCallback((path) => {
    navigate(path);
    setMenuOpen(false);
    setOpenGroup(null);
  }, [navigate]);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, display: "flex", flexDirection: "column" }}>
      {/* TopBar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "10px 16px" : "12px 28px",
          background: T.headerBg,
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {/* Left: Logo + Nav */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 20 }}>
          <img
            src="/logos/vq-logo-light.png"
            alt="ValueQuest"
            style={{ height: isMobile ? 24 : 30, width: "auto", cursor: "pointer" }}
            onClick={() => navTo("/")}
          />

          {/* Desktop: per-group nav dropdowns */}
          {!isMobile && visibleGroups.length > 0 && (
            <div ref={navRef} style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {visibleGroups.map((group) => (
                <div key={group} style={{ position: "relative" }}>
                  <button
                    onClick={() => {
                      const items = groupedApps[group];
                      // single-item group → open that item directly instead of a 1-row dropdown
                      if (items.length === 1) navTo(items[0].path);
                      else setOpenGroup((v) => (v === group ? null : group));
                    }}
                    style={{
                      border: "none",
                      padding: "8px 10px",
                      color: openGroup === group ? "#fff" : "rgba(255,255,255,0.85)",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      transition: "color 0.15s",
                      borderRadius: 6,
                      background: openGroup === group ? "rgba(255,255,255,0.12)" : "transparent",
                    }}
                    onMouseEnter={(e) => { if (openGroup !== group) e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { if (openGroup !== group) e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
                  >
                    {group}
                    {groupedApps[group].length > 1 && (
                      <span style={{ fontSize: 9, opacity: 0.7 }}>{openGroup === group ? "▲" : "▼"}</span>
                    )}
                  </button>

                  {openGroup === group && (
                    <div style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      background: T.surface,
                      border: `1px solid ${T.border}`,
                      borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      minWidth: 200,
                      zIndex: 200,
                      overflow: "hidden",
                    }}>
                      {groupedApps[group].map((app) => (
                        <div
                          key={app.id}
                          onClick={() => navTo(app.path)}
                          style={{
                            padding: "10px 16px",
                            cursor: "pointer",
                            fontSize: 13,
                            color: T.text,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            background: location.pathname === app.path ? "rgba(0,103,171,0.06)" : "transparent",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,103,171,0.04)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = location.pathname === app.path ? "rgba(0,103,171,0.06)" : "transparent")}
                        >
                          <span>{app.icon}</span>
                          <span>{app.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14 }}>
          {!isMobile && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>{name}</div>
          )}

          {/* Notification bell — reads only the signed-in user's own inbox */}
          <NotificationBell isMobile={isMobile} />

          {/* Profile avatar */}
          <div ref={profileRef} style={{ position: "relative" }}>
            <div
              onClick={() => setProfileOpen((v) => !v)}
              title="Profile & Groups"
              style={{
                width: 38,
                height: 38,
                minWidth: 38,
                borderRadius: 8,
                background: profileOpen ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12.5,
                fontWeight: 600,
                color: "#ffffff",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
            >
              {initials}
            </div>

            {profileOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  minWidth: isMobile ? 180 : 220,
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{name}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {account?.username || ""}
                  </div>
                </div>

                {groups.length > 0 && (
                  <div style={{ padding: "8px 16px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                      Groups
                    </div>
                    {groups.map((g) => {
                      const info = GROUP_LABELS[g];
                      return (
                        <div key={g} style={{ fontSize: 12, color: T.text, padding: "3px 0", display: "flex", alignItems: "center", gap: 6 }}>
                          <span>{info?.icon || "•"}</span>
                          <span>{info?.label || g}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout();
                  }}
                  style={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    fontSize: 13,
                    color: T.red,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(192,57,43,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  Sign out
                </div>
              </div>
            )}
          </div>

          {/* Mobile: hamburger menu */}
          {isMobile && (
            <div
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                width: 38,
                height: 38,
                minWidth: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#fff",
                fontSize: 20,
                borderRadius: 8,
                background: menuOpen ? "rgba(255,255,255,0.2)" : "transparent",
                transition: "background 0.15s",
              }}
            >
              {menuOpen ? "✕" : "☰"}
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu drawer */}
      {isMobile && menuOpen && (
        <div
          className="mobile-drawer"
          style={{
            position: "sticky",
            top: 58,
            zIndex: 99,
            background: T.surface,
            borderBottom: `1px solid ${T.border}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {visibleGroups.map((group) => (
            <div key={group} style={{ padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ padding: "8px 20px", fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {group}
              </div>
              {groupedApps[group].map((app) => (
                <div
                  key={app.id}
                  onClick={() => navTo(app.path)}
                  style={{
                    padding: "11px 20px",
                    cursor: "pointer",
                    fontSize: 14,
                    color: T.text,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: location.pathname === app.path ? "rgba(0,103,171,0.06)" : "transparent",
                  }}
                >
                  <span>{app.icon}</span>
                  <span>{app.label}</span>
                </div>
              ))}
            </div>
          ))}

          <div
            onClick={() => navTo("/")}
            style={{
              padding: "12px 20px",
              cursor: "pointer",
              fontSize: 14,
              color: T.primary,
              borderTop: `1px solid ${T.border}`,
              fontWeight: 500,
            }}
          >
            Home
          </div>
        </div>
      )}

      {/* Page content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Outlet />
      </div>
    </div>
  );
}
